// PageDrop — window-level drop target with a full-page "Release to use"
// overlay. Lets users drop files anywhere on the page, not just into a
// specific dropzone. Inner dropzones still take precedence (they call
// preventDefault on drop) so this only fires for drops on whitespace.
import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/** Watch window-level OS file drags. Returns whether a drag is in progress;
 *  fires the callback when files drop outside any inner dropzone. */
export function usePageDragWatcher(onFiles: (files: File[]) => void): boolean {
  const [active, setActive] = useState(false);
  const counterRef = useRef(0);
  const handlerRef = useRef(onFiles);
  handlerRef.current = onFiles;

  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");

    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      counterRef.current += 1;
      if (counterRef.current === 1) setActive(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      counterRef.current = Math.max(0, counterRef.current - 1);
      if (counterRef.current === 0) setActive(false);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      counterRef.current = 0;
      setActive(false);
      if (e.defaultPrevented) return; // an inner dropzone handled it
      e.preventDefault();
      const dropped = Array.from(e.dataTransfer?.files ?? []);
      if (dropped.length) handlerRef.current(dropped);
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return active;
}

interface OverlayProps {
  open: boolean;
  title?: string;
  hint?: string;
}

export function PageDropOverlay({ open, title = "Release to use", hint }: OverlayProps) {
  if (!open) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center duration-150 animate-in fade-in">
      <div
        className="absolute inset-0 bg-white/70 backdrop-blur-sm"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,255,255,0.55) 30%, hsl(var(--primary) / 0.28) 100%)",
        }}
      />
      <div className="absolute inset-4 animate-pulse rounded-3xl border-2 border-dashed border-primary/60 sm:inset-8" />
      <div className="relative flex flex-col items-center gap-3 text-center">
        <div className="rounded-full bg-white p-4 shadow-[0_12px_40px_-8px_hsl(var(--primary)/0.35)]">
          <Upload className="size-8 text-primary" aria-hidden />
        </div>
        <p className="font-display text-3xl font-medium text-foreground">{title}</p>
        {hint ? <p className="font-mono text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  );
}
